import React, { useEffect, useState } from "react";
import { Alert, Button, Spinner, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import { crmPostventaV2Api } from "./crmPostventaV2Api";
import "./CRMPostventaV2Procesos.css";

const makeToday = () => new Date().toISOString().slice(0, 10);

const buildExecutionSummary = (response) =>
  [
    response?.message || "Sin mensaje.",
    `Estado: ${response?.estado || "-"}`,
    `Candidatos: ${response?.candidatos || 0}`,
    `Creados: ${response?.creados || 0}`,
    `Omitidos: ${response?.omitidos || 0}`,
    `Errores: ${response?.errores || 0}`,
    ...(response?.details || []),
  ].join("\n");

export default function CRMPostventaV2Automation({ embedded = false }) {
  const [fechaReferencia, setFechaReferencia] = useState(makeToday());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const response = await crmPostventaV2Api.obtenerEstadoAutomatizacion();
      setStatus(response);
    } catch (error) {
      Swal.fire("Automatización", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const runProcess = async (procesoCodigo, dryRun) => {
    try {
      setSubmitting(true);
      if (dryRun) {
        const response = await crmPostventaV2Api.ejecutarAutomatizacion({
          procesoCodigo,
          dryRun: true,
          fechaReferencia,
        });
        await Swal.fire("Dry run completado", buildExecutionSummary(response), response?.success || response?.omitted ? "success" : "error");
      } else {
        const preview = await crmPostventaV2Api.ejecutarAutomatizacion({
          procesoCodigo,
          dryRun: true,
          fechaReferencia,
        });
        const confirmation = await Swal.fire({
          title: "Confirmar ejecución real",
          text: [
            buildExecutionSummary(preview),
            "",
            `Se crearán ${preview?.creados || 0} procesos reales en esta ejecución.`,
          ].join("\n"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Ejecutar ahora",
          cancelButtonText: "Cancelar",
        });

        if (!confirmation.isConfirmed) {
          return;
        }

        const response = await crmPostventaV2Api.ejecutarAutomatizacion({
          procesoCodigo,
          dryRun: false,
          fechaReferencia,
        });
        await Swal.fire("Ejecución completada", buildExecutionSummary(response), response?.success || response?.omitted ? "success" : "error");
      }

      await loadStatus();
    } catch (error) {
      Swal.fire("Automatización", error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <>
      <section className={`crm-postventa-v2-panel p-4${embedded ? "" : " mt-4"}`}>
        <div className="d-flex flex-wrap gap-3 justify-content-between align-items-end">
          <div>
            <div className="crm-postventa-kicker">{embedded ? "Seguimiento 2" : "CRM Postventa V2"}</div>
            <h2 className="mb-1">Automatización</h2>
            <p className="text-muted mb-0">Scheduler interno seguro con historial, dry run y ejecución manual controlada.</p>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-end">
            <label className="d-flex flex-column gap-1">
              <span className="small fw-semibold">Fecha referencia</span>
              <input
                className="form-control"
                type="date"
                value={fechaReferencia}
                onChange={(event) => setFechaReferencia(event.target.value)}
              />
            </label>
            <Button variant="outline-secondary" onClick={loadStatus} disabled={loading || submitting}>
              Actualizar
            </Button>
          </div>
        </div>
      </section>

      <section className="crm-postventa-v2-panel p-4 mt-4">
        {loading ? (
          <div className="py-5 text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <Alert variant={status?.enabled ? "success" : "warning"}>
              Scheduler global: <strong>{status?.enabled ? "habilitado" : "deshabilitado"}</strong> | Dry run only:{" "}
              <strong>{status?.dryRunOnly ? "sí" : "no"}</strong> | Zona horaria: <strong>{status?.timeZone || "-"}</strong>
            </Alert>

            <div className="row g-3">
              {(status?.processes || []).map((process) => (
                <div className="col-12 col-xl-6" key={process.procesoCodigo}>
                  <div className="crm-postventa-v2-card h-100">
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <h5 className="mb-1">{process.nombre}</h5>
                        <div className="small text-muted">{process.procesoCodigo}</div>
                      </div>
                      <span className={`badge ${process.enabled ? "bg-success" : "bg-secondary"}`}>
                        {process.enabled ? "Activo" : "Pausado"}
                      </span>
                    </div>

                    <div className="small mt-3 crm-postventa-v2-meta-grid">
                      <div><strong>Frecuencia:</strong> {process.frecuencia}</div>
                      <div><strong>Próxima:</strong> {process.nextRun}</div>
                      <div><strong>Última:</strong> {process.lastRun}</div>
                      <div><strong>Estado:</strong> {process.lastState}</div>
                      <div className="crm-postventa-v2-meta-span"><strong>Resumen:</strong> {process.lastSummary}</div>
                    </div>

                    <div className="d-flex gap-2 mt-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={submitting}
                        onClick={() => runProcess(process.procesoCodigo, true)}
                      >
                        Dry run
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        disabled={submitting}
                        onClick={() => runProcess(process.procesoCodigo, false)}
                      >
                        Ejecutar ahora
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <h4 className="mb-3">Historial reciente</h4>
              <Table hover responsive className="align-middle crm-postventa-v2-table">
                <thead>
                  <tr>
                    <th>Proceso</th>
                    <th>Inicio</th>
                    <th>Fin</th>
                    <th>Dry run</th>
                    <th>Estado</th>
                    <th>Candidatos</th>
                    <th>Creados</th>
                    <th>Omitidos</th>
                    <th>Errores</th>
                    <th>Resumen</th>
                  </tr>
                </thead>
                <tbody>
                  {(status?.history || []).length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center text-muted py-4">
                        Todavía no hay ejecuciones registradas.
                      </td>
                    </tr>
                  ) : (
                    (status?.history || []).map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="fw-semibold">{item.procesoNombre}</div>
                          <div className="small text-muted">{item.procesoCodigo}</div>
                        </td>
                        <td>{item.fechaInicio}</td>
                        <td>{item.fechaFin}</td>
                        <td>{item.dryRun ? "Sí" : "No"}</td>
                        <td>{item.estado}</td>
                        <td>{item.candidatos}</td>
                        <td>{item.creados}</td>
                        <td>{item.omitidos}</td>
                        <td>{item.errores}</td>
                        <td>{item.detalleResumen}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </section>
    </>
  );

  return embedded ? content : (
    <section className="crm-postventa-page" translate="no">
      <div className="container py-4">{content}</div>
    </section>
  );
}
